'use client';

import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';

type DutyEvent = 'INSERT' | 'UPDATE' | 'DELETE';

interface RealtimeDutyPayload {
  eventType: DutyEvent;
  new: Record<string, any>;
  old: Record<string, any>;
}

function buildToastMessage(payload: RealtimeDutyPayload): { message: string; type: 'success' | 'info' | 'warning' | 'error' } {
  const { eventType, new: newRow, old: oldRow } = payload;
  const row = newRow ?? oldRow ?? {};
  const dutyCode = row.duty_code ?? 'Duty';
  const route = row.route ?? '';
  const driver = row.driver1_name ?? '';
  const conductor = row.conductor_name ?? '';
  const status = row.status ?? '';

  if (eventType === 'INSERT') {
    const crew = [driver, conductor].filter((n) => n && n !== '—').join(' & ');
    return {
      message: `📋 ${dutyCode} assigned${route ? ` on ${route}` : ''}${crew ? ` — ${crew}` : ''}`,
      type: 'success',
    };
  }

  if (eventType === 'DELETE') {
    return {
      message: `🗑️ ${dutyCode} removed${route ? ` (${route})` : ''}`,
      type: 'warning',
    };
  }

  // UPDATE — detect what changed
  const prevStatus = oldRow?.status ?? '';
  if (status && status !== prevStatus) {
    const icons: Record<string, string> = {
      Active: '🟢',
      Completed: '✅',
      Cancelled: '🚫',
      Delayed: '⚠️',
      Scheduled: '📅',
    };
    const icon = icons[status] ?? '🔄';
    return {
      message: `${icon} ${dutyCode}${route ? ` (${route})` : ''} → ${status}`,
      type: status === 'Cancelled' ? 'error' : status === 'Delayed' ? 'warning' : 'info',
    };
  }

  // Generic assignment update (driver/conductor changed)
  const crew = [driver, conductor].filter((n) => n && n !== '—').join(' & ');
  return {
    message: `✏️ ${dutyCode}${route ? ` (${route})` : ''} updated${crew ? ` — ${crew}` : ''}`,
    type: 'info',
  };
}

/**
 * Subscribes to duty_assignments real-time events.
 * Fires toast notifications and calls onRefresh() so the caller can re-fetch data.
 */
export function useRealtimeDutyToast(onRefresh: () => void) {
  const onRefreshRef = useRef(onRefresh);
  useEffect(() => { onRefreshRef.current = onRefresh; }, [onRefresh]);

  useEffect(() => {
    const supabase = createClient();

    const channel = supabase
      .channel('duty_realtime_toast')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'duty_assignments' },
        (payload) => {
          const { message, type } = buildToastMessage({
            eventType: payload.eventType as DutyEvent,
            new: (payload.new as Record<string, any>) ?? {},
            old: (payload.old as Record<string, any>) ?? {},
          });

          switch (type) {
            case 'success': toast.success(message, { duration: 4000 }); break;
            case 'warning': toast.warning(message, { duration: 4000 }); break;
            case 'error':   toast.error(message,   { duration: 4000 }); break;
            default:        toast.info(message,    { duration: 4000 }); break;
          }

          // Debounce refresh — wait a tick so DB is consistent
          setTimeout(() => onRefreshRef.current(), 400);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);
}
